import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { environment } from '../../environments/environment';

export interface OAuthTokenResponse {
    access_token: string;
    refresh_token?: string;
    scope?: string;
    token_type: string;
    expires_in: number;
    hasMFA?: boolean;
}

interface FlinkJwtPayload extends JwtPayload {
    [key: string]: unknown;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly accessTokenKey = 'access_token';
    private readonly refreshTokenKey = 'refresh_token';
    private readonly firstNameKey = 'firstName';
    private readonly loginEndpoint = `${environment.REST_API_SERVER}xflkauth/api/v1/flink/auth/login`;
    private readonly refreshEndpoint = `${environment.REST_API_SERVER}xflkauth/api/v1/flink/auth/refresh`;
    private refreshRequest?: Observable<string>;
    private sessionGeneration = 0;

    constructor(private http: HttpClient) { }

    login(username: string, password: string): Observable<void> {
        this.clearSession();
        const generation = this.sessionGeneration;
        const payload = { username, password };

        return this.requestToken(this.loginEndpoint, payload).pipe(
            tap(response => this.storeTokenResponse(response, true, generation)),
            map(() => undefined)
        );
    }

    refreshAccessToken(): Observable<string> {
        if (this.refreshRequest) {
            return this.refreshRequest;
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            return throwError(() => new Error('Refresh token não disponível'));
        }

        const payload = { refresh_token: refreshToken };

        const generation = this.sessionGeneration;
        this.refreshRequest = this.requestToken(this.refreshEndpoint, payload).pipe(
            tap(response => this.storeTokenResponse(response, false, generation)),
            map(response => response.access_token),
            finalize(() => this.refreshRequest = undefined),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        return this.refreshRequest;
    }

    getAccessToken(): string | null {
        return sessionStorage.getItem(this.accessTokenKey);
    }

    getRefreshToken(): string | null {
        return sessionStorage.getItem(this.refreshTokenKey);
    }

    hasValidAccessToken(): boolean {
        const token = this.getAccessToken();
        return Boolean(token && !this.tokenExpired(token));
    }

    canRefreshToken(): boolean {
        return Boolean(this.getRefreshToken());
    }

    getFirstName(): string {
        return sessionStorage.getItem(this.firstNameKey) || '';
    }

    setFirstName(fullName: string): void {
        const firstName = this.formatFirstName(fullName);
        if (firstName) {
            sessionStorage.setItem(this.firstNameKey, firstName);
        }
    }

    getTokenSubject(): string {
        const token = this.getAccessToken();
        const payload = token ? this.decodeToken(token) : null;
        return typeof payload?.sub === 'string' ? payload.sub : '';
    }

    getTokenClaims(): Readonly<FlinkJwtPayload> {
        const token = this.getAccessToken();
        return token ? this.decodeToken(token) || {} : {};
    }

    clearSession(): void {
        this.sessionGeneration++;
        this.refreshRequest = undefined;
        sessionStorage.removeItem(this.accessTokenKey);
        sessionStorage.removeItem(this.refreshTokenKey);
        sessionStorage.removeItem(this.firstNameKey);

        // Remove tokens persisted by older versions of the frontend.
        localStorage.removeItem(this.accessTokenKey);
        localStorage.removeItem(this.refreshTokenKey);
        localStorage.removeItem('firstName');
    }

    clearStorage(): void {
        this.clearSession();
    }

    tokenExpired(token: string): boolean {
        const payload = this.decodeToken(token);
        return !payload?.exp || Math.floor(Date.now() / 1000) >= payload.exp;
    }

    setDataInLocalStorage(variableName: string, data: unknown): void {
        localStorage.setItem(variableName, String(data));
    }

    removeDataInLocalStorage(variableName: string): void {
        localStorage.removeItem(variableName);
    }

    getParam(param: string): string | null {
        return localStorage.getItem(param);
    }

    private requestToken(endpoint: string, payload: object): Observable<OAuthTokenResponse> {
        return this.http.post<OAuthTokenResponse>(endpoint, payload, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Cache-Control': 'no-store',
                Pragma: 'no-cache',
                // A tela de login apresenta uma mensagem segura e contextual.
                // Impede que o interceptor do PO UI exiba detalhes da autenticação.
                'X-PO-No-Message': 'true'
            }
        });
    }

    private storeTokenResponse(
        response: OAuthTokenResponse,
        requireRefreshToken: boolean,
        generation: number
    ): void {
        if (generation !== this.sessionGeneration) {
            throw new Error('Sessão encerrada');
        }

        if (
            !response?.access_token ||
            response.token_type?.toLowerCase() !== 'bearer' ||
            this.tokenExpired(response.access_token)
        ) {
            this.clearSession();
            throw new Error('Resposta de autenticação inválida');
        }

        if (requireRefreshToken && !response.refresh_token) {
            this.clearSession();
            throw new Error('Refresh token não retornado pelo servidor');
        }

        sessionStorage.setItem(this.accessTokenKey, response.access_token);

        // OAuth2 pode rotacionar o refresh token. Quando não vier um novo,
        // preservamos o token atual.
        if (response.refresh_token) {
            sessionStorage.setItem(this.refreshTokenKey, response.refresh_token);
        }
    }

    private decodeToken(token: string): FlinkJwtPayload | null {
        try {
            return jwtDecode<FlinkJwtPayload>(token);
        } catch {
            return null;
        }
    }

    private formatFirstName(fullName: string): string {
        const firstName = String(fullName || '').trim().split(/\s+/)[0] || '';
        return firstName
            ? firstName.charAt(0).toLocaleUpperCase('pt-BR') +
              firstName.slice(1).toLocaleLowerCase('pt-BR')
            : '';
    }
}
